import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { CURRENCY, setVendorScopedCurrency } from '../config/currency';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import ImprovedGuestVerificationModal from '../components/ImprovedGuestVerificationModal';
import PaymentDifferenceModal from '../components/PaymentDifferenceModal';
import OrderConfirmationDialog from '../components/OrderConfirmationDialog';
import SessionConflictModal from '../components/SessionConflictModal';
import SpiceLevelBadge from '../components/SpiceLevelBadge';
import MenuItemImage from '../components/MenuItemImage';
import { DietaryDot } from '../components/DietaryDot';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Chip, LinearProgress } from '@mui/material';

interface LocationState {
  vendorId: string
  qrCode: string
  orderId?: number;
  deliveryType?: 'DELIVER_TO_TABLE' | 'COLLECT';
  isModification?: boolean; // Explicit flag for order modification
}

interface Topping { 
  id: number; 
  name: string 
}

interface MenuItemDef {
  id: number;
  name: string;
  description?: string;
  toppings?: Topping[];
  freeToppingsCount?: number;
  extraToppingPrice?: number;
  quantity?: number;
  available: boolean;
  imageUrl?: string
}

interface MenuCategory { 
  id: number; 
  name: string; 
  items: MenuItemDef[] 
}

interface OrderModificationState {
  isModifying: boolean;
  originalOrderId: number | null;
  originalOrderStatus: string | null;
  canModify: boolean;
  warningMessage: string | null;
}

interface LoyaltyProgress {
  id: number;
  programId: number;
  programName: string;
  programType: string;
  programSummary: string;
  vendorId: number;
  vendorName: string;
  qualifyingVisits: number;
  qualifyingSpend: number;
  pointsBalance: number;
  itemPurchaseCount: number;
  rewardAvailable: boolean;
  rewardValue: number | null;
  rewardEarnedAt: string | null;
  rewardExpiresAt: string | null;
  progressPercentage: number;
  progressDisplay: string;
  remainingToReward: number;
  cyclesCompleted: number;
  badgeColor: string;
  badgeIcon: string;
  rewardType: string;
  rewardDescription?: string;
}

// Item styling for modern theme - soft/subtle colors
const itemKeywordStyles: { [key: string]: { emoji: string; color: string; gradient: string } } = {
  // Beverages - soft tones
  coffee: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  cappuccino: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  latte: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  tea: { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF530 0%, #D1FAE520 100%)' },
  juice: { emoji: '🧃', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  smoothie: { emoji: '🥤', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  shake: { emoji: '🥛', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  soda: { emoji: '🥤', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
  water: { emoji: '💧', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF30 0%, #DBEAFE20 100%)' },
  // Main dishes - soft tones
  pizza: { emoji: '🍕', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
  burger: { emoji: '🍔', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  sandwich: { emoji: '🥪', color: '#7BA868', gradient: 'linear-gradient(135deg, #F7FEE730 0%, #ECFCCB20 100%)' },
  pasta: { emoji: '🍝', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
  noodles: { emoji: '🍜', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
  rice: { emoji: '🍚', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB30 0%, #F3F4F620 100%)' },
  biryani: { emoji: '🍛', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  curry: { emoji: '🍛', color: '#B07858', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  chicken: { emoji: '🍗', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  fish: { emoji: '🐟', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF30 0%, #DBEAFE20 100%)' },
  steak: { emoji: '🥩', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
  soup: { emoji: '🍲', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  // Appetizers & Sides
  salad: { emoji: '🥗', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF430 0%, #DCFCE720 100%)' },
  fries: { emoji: '🍟', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
  wings: { emoji: '🍗', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
  // Desserts - soft pink
  cake: { emoji: '🍰', color: '#A87898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  icecream: { emoji: '🍨', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  ice: { emoji: '🍨', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  chocolate: { emoji: '🍫', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  cookie: { emoji: '🍪', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  donut: { emoji: '🍩', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
  // Breakfast
  egg: { emoji: '🍳', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
  pancake: { emoji: '🥞', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  waffle: { emoji: '🧇', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  bread: { emoji: '🍞', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  toast: { emoji: '🍞', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  // Snacks
  popcorn: { emoji: '🍿', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
  chips: { emoji: '🥔', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
  nachos: { emoji: '🌮', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
  // Default
  default: { emoji: '🍽️', color: '#6B7280', gradient: 'linear-gradient(135deg, #EEF2FF30 0%, #E0E7FF20 100%)' }
};

const getItemStyle = (itemName: string) => {
  const lowerName = itemName.toLowerCase();
  for (const [keyword, style] of Object.entries(itemKeywordStyles)) {
    if (keyword !== 'default' && lowerName.includes(keyword)) {
      return style;
    }
  }
  // Fallback: assign soft color based on first letter
  const firstLetter = lowerName.charAt(0);
  const colorMap: { [key: string]: { emoji: string; color: string; gradient: string } } = {
    a: { emoji: '🍎', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
    b: { emoji: '🥯', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
    c: { emoji: '🥕', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
    d: { emoji: '🍩', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
    e: { emoji: '🥚', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
    f: { emoji: '🍟', color: '#7BA868', gradient: 'linear-gradient(135deg, #F7FEE730 0%, #ECFCCB20 100%)' },
    g: { emoji: '🍇', color: '#9B8AB8', gradient: 'linear-gradient(135deg, #FAF5FF30 0%, #F3E8FF20 100%)' },
    h: { emoji: '🍯', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
    i: { emoji: '🍨', color: '#6B98A8', gradient: 'linear-gradient(135deg, #F0FDFA30 0%, #CCFBF120 100%)' },
    j: { emoji: '🧃', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F830 0%, #FCE7F320 100%)' },
    k: { emoji: '🥝', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF430 0%, #DCFCE720 100%)' },
    l: { emoji: '🍋', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
    m: { emoji: '🥭', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
    n: { emoji: '🥜', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEF3C720 100%)' },
    o: { emoji: '🍊', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED30 0%, #FEECD620 100%)' },
    p: { emoji: '🍑', color: '#B88888', gradient: 'linear-gradient(135deg, #FEF7F730 0%, #FDE8E820 100%)' },
    q: { emoji: '🧀', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE830 0%, #FEF3C720 100%)' },
    r: { emoji: '🍓', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
    s: { emoji: '🍓', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
    t: { emoji: '🍅', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F230 0%, #FDDEDE20 100%)' },
    u: { emoji: '🍜', color: '#9B8AB8', gradient: 'linear-gradient(135deg, #FAF5FF30 0%, #F3E8FF20 100%)' },
    v: { emoji: '🥬', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF430 0%, #DCFCE720 100%)' },
    w: { emoji: '🍉', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF530 0%, #D1FAE520 100%)' },
    x: { emoji: '✨', color: '#6B7280', gradient: 'linear-gradient(135deg, #EEF2FF30 0%, #E0E7FF20 100%)' },
    y: { emoji: '🍠', color: '#9888A8', gradient: 'linear-gradient(135deg, #FAF5FF30 0%, #F3E8FF20 100%)' },
    z: { emoji: '🥒', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF430 0%, #DCFCE720 100%)' },
  };
  return colorMap[firstLetter] || itemKeywordStyles.default;
};

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { user, guestSession, isGuest, isAuthenticated, signin, token, validateSessionWithBackend } = useAuth();
  const { theme, currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';
  const { state } = useLocation();
  const { state: cartState, updateItemComment, updateItemToppings, updateQuantity, removeFromCart, clearCart, setPendingOrderId, clearCartAfterOrderPlacement, addToCart } = useCart();
  const { showToast } = useToast();
  const { vendorId, qrCode, orderId, deliveryType: initDelivery, isModification } = (state as LocationState) || {};
  
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
  const [deliveryType, setDeliveryType] = useState<'DELIVER_TO_TABLE' | 'COLLECT'>(
    initDelivery || 'DELIVER_TO_TABLE'
  );
  const [specialInstructions, setSpecialInstructions] = useState('');
  // Allergy declaration (UK plan Phase 4) — printed highlighted on the kitchen ticket
  const [allergyNote, setAllergyNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTableOrder, setIsTableOrder] = useState<boolean | null>(null);
  const [tableInfo, setTableInfo] = useState<{ id?: number; name?: string } | null>(null);
  const [allowGuestTableOrders, setAllowGuestTableOrders] = useState<boolean>(true); // Default to true

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showGuestNameModal, setShowGuestNameModal] = useState(false); // Simple guest name modal for allowed guest table orders
  const [guestName, setGuestName] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [showSessionConflictModal, setShowSessionConflictModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  
  // Login form state
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // NEW: Order confirmation flag for the new flow
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [processingOrderId, setProcessingOrderId] = useState<number | null>(null);
  
  // New state for order modification management
  const [modificationState, setModificationState] = useState<OrderModificationState>({
    isModifying: false,
    originalOrderId: null,
    originalOrderStatus: null,
    canModify: false,
    warningMessage: null
  });

  // Tax preview state
  const [taxPreview, setTaxPreview] = useState<{
    subtotal: number;
    cgstRate: number;
    cgstAmount: number;
    sgstRate: number;
    sgstAmount: number;
    igstRate: number;
    igstAmount: number;
    serviceChargeRate: number;
    serviceChargeAmount: number;
    discountAmount: number;
    taxType: string;
    total: number;
  } | null>(null);
  const [taxLoading, setTaxLoading] = useState(false);

  // Loyalty rewards state
  const [loyaltyProgress, setLoyaltyProgress] = useState<LoyaltyProgress[]>([]);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [showLoyaltyDetails, setShowLoyaltyDetails] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize modification state and validate order
  useEffect(() => {
    if (orderId && isModification) {
      console.log('Initializing order modification for order:', orderId);
      
      api.get<{ id: number; status: string; deliveryType: 'DELIVER_TO_TABLE' | 'COLLECT'; vendor?: { id: number } }>(`/orders/${orderId}`)
        .then(res => {
          const order = res.data;
          console.log('Order details for modification:', order);
          
          // Check if order can still be modified (PENDING or ACCEPTED)
          const canModify = order.status === 'PENDING' || order.status === 'ACCEPTED';
          const warningMessage = !canModify 
            ? `This order is ${order.status.toLowerCase()} and cannot be modified. You can create a new order instead.`
            : null;
          
          setModificationState({
            isModifying: true,
            originalOrderId: orderId,
            originalOrderStatus: order.status,
            canModify,
            warningMessage
          });
          
          setDeliveryType(order.deliveryType);
          
          // If order cannot be modified, clear the pending order ID to prevent auto-updates
          if (!canModify) {
            setPendingOrderId(null);
          }
        })
        .catch(err => {
          console.error('Failed to load order for modification:', err);
          setError('Failed to load order details. Please try again.');
          setModificationState({
            isModifying: false,
            originalOrderId: null,
            originalOrderStatus: null,
            canModify: false,
            warningMessage: 'Order not found or no longer available for modification.'
          });
        });
    } else {
      // Clear modification state for new orders
      setModificationState({
        isModifying: false,
        originalOrderId: null,
        originalOrderStatus: null,
        canModify: false,
        warningMessage: null
      });
    }
  }, [orderId, isModification, setPendingOrderId]);
  
  // Handle timeout for modification loading state
  useEffect(() => {
    if (isModification && orderId && cartState.items.length === 0) {
      // After 2 seconds, if cart is still empty, this means hydration likely failed
      const timeoutId = setTimeout(() => {
        console.log('Cart hydration timeout - cart is still empty after 2 seconds');
        setError('Failed to load order items. Please try again.');
      }, 2000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isModification, orderId, cartState.items.length]);

  // Fetch menu data - also refetch when cart state changes to ensure we have latest menu data
  useEffect(() => {
    const currentQrCode = cartState.qrCode || qrCode;
    const currentVendorId = cartState.vendorId || vendorId;
    
    if (currentQrCode) {
      api.get<{
        vendorId: number;
        vendorName: string;
        tableId?: number;
        tableName?: string;
        qrCode: string;
        categories: MenuCategory[];
        allowGuestTableOrders?: boolean;
        currencySymbol?: string;
        currencyCode?: string;
        locale?: string;
      }>(`/qr/${currentQrCode}/menu`)
        .then(res => {
          // Adopt the restaurant's currency for this tab (guest QR context)
          setVendorScopedCurrency({
            currencySymbol: res.data.currencySymbol,
            currencyCode: res.data.currencyCode,
            locale: res.data.locale,
          });
          setMenuCategories(res.data.categories || []);
          // Determine if this is a table order based on presence of tableId
          const hasTable = !!(res.data.tableId || res.data.tableName);
          setIsTableOrder(hasTable);

          // Store table information if available
          if (hasTable) {
            setTableInfo({
              id: res.data.tableId,
              name: res.data.tableName
            });
          } else {
            setTableInfo(null);
          }

          // Store vendor's guest table order setting (default to true if not specified)
          setAllowGuestTableOrders(res.data.allowGuestTableOrders !== false);

          // Set delivery type based on QR code type
          // If it's a table order, default to DELIVER_TO_TABLE
          // If it's a counter order (no table), default to COLLECT
          if (!initDelivery) {
            setDeliveryType(hasTable ? 'DELIVER_TO_TABLE' : 'COLLECT');
          }
        })
        .catch(console.error);
    } else if (currentVendorId) {
      api.get<MenuCategory[]>(`/vendors/${currentVendorId}/menu/categories`)
        .then(res => {
          setMenuCategories(res.data);
          // When using vendorId directly (not QR), assume it's a counter order
          setIsTableOrder(false);
          setTableInfo(null);
          if (!initDelivery) {
            setDeliveryType('COLLECT');
          }
        })
        .catch(console.error);
    }
  }, [qrCode, vendorId, cartState.qrCode, cartState.vendorId, cartState.items.length]);

  // Calculate total dynamically on every render - no memoization to avoid stale data
  const total = cartState.items.reduce((sum, ci) => {
    const base = ci.price * ci.quantity;
    // Add safety check for menuCategories
    const itemDef = Array.isArray(menuCategories) && menuCategories.length > 0
      ? menuCategories.flatMap(cat => cat.items || []).find(it => it.id === ci.menuItem.id)
      : null;
    let extra = 0;
    if (itemDef && ci.toppings) {
      const freeCount = itemDef.freeToppingsCount || 0;
      const extraPrice = itemDef.extraToppingPrice || 0;
      const count = ci.toppings.length;
      extra = Math.max(0, count - freeCount) * extraPrice * ci.quantity;
    }
    return sum + base + extra;
  }, 0);

  // Fetch tax preview whenever cart changes
  useEffect(() => {
    const fetchTaxPreview = async () => {
      // Use vendorId from URL params or cart context
      const effectiveVendorId = vendorId || cartState.vendorId;

      if (cartState.items.length === 0) {
        setTaxPreview(null);
        return;
      }

      // If no vendor ID, try to get it from the first item's menu
      let resolvedVendorId = effectiveVendorId;
      if (!resolvedVendorId && cartState.items.length > 0) {
        // Extract vendor ID from the first item if available
        resolvedVendorId = cartState.items[0].menuItem?.vendorId || 2; // Default to vendor 2 (Grand Hotel) for testing
      }

      if (!resolvedVendorId) {
        console.warn('No vendor ID available for tax calculation');
        setTaxPreview(null);
        return;
      }

      setTaxLoading(true);
      try {
        const response = await api.post('/api/orders/calculate-taxes', {
          vendorId: parseInt(resolvedVendorId.toString()),
          items: cartState.items.map(item => ({
            menuItemId: item.menuItem.id,
            quantity: item.quantity,
            price: item.price,
            portionName: item.portion
          }))
        });

        setTaxPreview(response.data);
        console.log('Tax preview fetched successfully:', response.data);
      } catch (err: any) {
        console.error('Error fetching tax preview:', err);
        // Log more details for debugging
        if (err.response) {
          console.error('Tax API error response:', err.response.data);
          console.error('Tax API error status:', err.response.status);
        }
        // Set a default tax structure with 0 values to show the UI structure
        setTaxPreview({
          subtotal: total,
          cgstRate: 2.5,
          cgstAmount: 0,
          sgstRate: 2.5,
          sgstAmount: 0,
          igstRate: 0,
          igstAmount: 0,
          serviceChargeRate: 10,
          serviceChargeAmount: 0,
          discountAmount: 0,
          totalTax: 0,
          grandTotal: total
        });
      } finally {
        setTaxLoading(false);
      }
    };

    fetchTaxPreview();
  }, [cartState.items, vendorId, cartState.vendorId, total]);

  // Fetch loyalty progress for the current vendor
  useEffect(() => {
    const fetchLoyaltyProgress = async () => {
      const effectiveVendorId = vendorId || cartState.vendorId;

      // Only fetch if user is authenticated and we have a vendor ID
      if (!isAuthenticated || !effectiveVendorId || isGuest) {
        setLoyaltyProgress([]);
        return;
      }

      setLoyaltyLoading(true);
      try {
        const response = await api.get('/api/loyalty/customer/rewards');
        // Filter to only show rewards for the current vendor
        const vendorRewards = response.data.filter(
          (reward: LoyaltyProgress) => reward.vendorId === parseInt(effectiveVendorId.toString())
        );
        setLoyaltyProgress(vendorRewards);
      } catch (error) {
        console.error('Error fetching loyalty progress:', error);
        setLoyaltyProgress([]);
      } finally {
        setLoyaltyLoading(false);
      }
    };

    fetchLoyaltyProgress();
  }, [vendorId, cartState.vendorId, isAuthenticated, isGuest]);

  // Debug logging (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log("=== CHECKOUT PAGE DEBUG ===");
      console.log("Cart items:", cartState.items);
      console.log("Modification state:", modificationState);
      console.log("URL state:", { vendorId, qrCode, orderId, deliveryType: initDelivery, isModification });
      console.log("isValidModification check:", {
        isModifying: modificationState.isModifying,
        canModify: modificationState.canModify,
        originalOrderId: modificationState.originalOrderId,
        result: modificationState.isModifying && modificationState.canModify && modificationState.originalOrderId
      });
      console.log("Calculated total:", total);
      console.log("Menu categories loaded:", menuCategories.length);
      console.log("Vendor context:", { vendorId: cartState.vendorId, qrCode: cartState.qrCode });
      console.log("========================");
    }
  }, [cartState.items, modificationState, total, menuCategories.length, vendorId, qrCode, orderId, initDelivery, isModification]);

  const handleQuantityChange = (itemId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
    } else {
      const itemDef = menuCategories.flatMap(cat => cat.items).find(it => it.id === itemId);
      const maxQty = itemDef?.quantity;
      const finalQty = maxQty != null ? Math.min(newQuantity, maxQty) : newQuantity;
      updateQuantity(itemId, finalQty);
    }
  };

  // Helper function for portion-specific quantity changes
  const handlePortionQuantityChange = (cartItem: typeof cartState.items[0], delta: number) => {
    const currentQuantity = cartItem.quantity;
    const newQuantity = currentQuantity + delta;
    
    if (newQuantity <= 0) {
      // Remove this specific portion
      const allItemsForThisMenu = cartState.items.filter(item => item.menuItem.id === cartItem.menuItem.id);
      const otherPortions = allItemsForThisMenu.filter(item => item.portionId !== cartItem.portionId);
      
      // Clear all items for this menu item
      removeFromCart(cartItem.menuItem.id);
      
      // Re-add only the other portions
      otherPortions.forEach(item => {
        addToCart({
          id: item.menuItem.id,
          name: item.menuItem.name,
          price: item.price,
          portionId: item.portionId,
          portionName: item.portionName,
          selectedToppings: item.selectedToppings,
          freeToppingsCount: item.freeToppingsCount,
          requestedSpiceLevel: item.requestedSpiceLevel
        }, item.quantity);
      });
    } else {
      // Update this portion (both increase and decrease)
      const allItemsForThisMenu = cartState.items.filter(item => item.menuItem.id === cartItem.menuItem.id);
      const updatedItems = allItemsForThisMenu.map(item => {
        if (item.portionId === cartItem.portionId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      
      // Clear all items for this menu item
      removeFromCart(cartItem.menuItem.id);
      
      // Re-add all items with updated quantities
      updatedItems.forEach(item => {
        if (item.quantity > 0) {
          addToCart({
            id: item.menuItem.id,
            name: item.menuItem.name,
            price: item.price,
            portionId: item.portionId,
            portionName: item.portionName,
            selectedToppings: item.selectedToppings,
            freeToppingsCount: item.freeToppingsCount,
            requestedSpiceLevel: item.requestedSpiceLevel
          }, item.quantity);
        }
      });
    }
  };

  const handleStartNewOrder = () => {
    // Clear modification state and start fresh
    setModificationState({
      isModifying: false,
      originalOrderId: null,
      originalOrderStatus: null,
      canModify: false,
      warningMessage: null
    });
    setPendingOrderId(null);
    setError(null);
    
    // Keep current cart items but treat as new order
    console.log('Starting new order with current cart items');
  };

  const handleLogin = async () => {
    if (!loginUsername.trim() || !loginPassword.trim()) {
      setAuthError('Please enter both username and password');
      return;
    }
    
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      await signin(loginUsername, loginPassword);
      setShowAuthModal(false);
      // Show confirmation dialog after successful login
      setTimeout(() => setShowConfirmationDialog(true), 100);
    } catch (err) {
      setAuthError(err.response?.data?.message || err.message || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGuestSuccess = () => {
    setShowGuestModal(false);
    setShowAuthModal(false);
    // Show confirmation dialog after successful guest verification
    setTimeout(() => setShowConfirmationDialog(true), 100);
  };

  const handlePaymentComplete = async (success: boolean, message: string) => {
    if (success) {
      // Payment was successful, now proceed with the order modification
      try {
        setLoading(true);
        
        const payloadItems = cartState.items.map(ci => ({
          menuItem: { id: ci.menuItem.id },
          quantity: ci.quantity,
          price: ci.price,
          portionId: ci.portionId, // Add portion ID for backend
          comment: ci.comment || '', // Only use item-level comments
          toppings: ci.toppings?.map(tid => ({ id: tid }))
        }));

        const response = await api.put(`/orders/${modificationState.originalOrderId}`, {
          items: payloadItems,
          deliveryType,
          orderNotes: specialInstructions || undefined, // Pass as order-level notes
        });
        
        const newOrderId = response.data.id;
        
        // Clear cart and navigate to success page
        clearCartAfterOrderPlacement(newOrderId.toString());
        setPendingOrderId(null);
        setLoading(false);
        
        // Add a small delay before navigation to ensure backend transaction commits
        setTimeout(() => {
          navigate(`/order-success/${newOrderId}`, { 
            replace: true,
            state: { 
              isModification: true,
              message: message || 'Order updated successfully with payment processed!'
            }
          });
        }, 100); // 100ms delay
        
      } catch (err) {
        console.error("Order modification failed after payment:", err);
        setError("Order modification failed. Please contact support.");
        setLoading(false);
      }
    } else {
      setError("Payment failed. Please try again.");
    }
  };

  // Simple guest name modal for when guest table orders are allowed
  const renderGuestNameModal = () => {
    if (!showGuestNameModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Continue as Guest
            </h2>
            <button
              onClick={() => setShowGuestNameModal(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Enter your name so we can identify your order (optional).
          </p>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Your Name
            </label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="e.g., John"
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowGuestNameModal(false)}
              className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleGuestNameSubmit}
              className="flex-1 py-2.5 bg-[color:var(--action)] hover:bg-[color:var(--action-hover)] text-[color:var(--action-text)] rounded-lg transition-colors font-medium"
            >
              Continue
            </button>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setShowGuestNameModal(false);
                setShowAuthModal(true);
              }}
              className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Or login with your account
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAuthModal = () => {
    if (!showAuthModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Complete Your Order
            </h2>
            <button
              onClick={() => setShowAuthModal(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            To place your order, please login to your account or continue as a guest.
          </p>
          
          {/* Guest Option - Simple guest ONLY for table orders with allowGuestTableOrders, phone verification otherwise */}
          {/* For counter orders (non-table), always require phone verification */}
          {(isTableOrder === true || tableInfo !== null) && allowGuestTableOrders ? (
            <button
              onClick={() => {
                setShowAuthModal(false);
                setShowGuestNameModal(true);
              }}
              className="w-full mb-4 py-3 bg-[color:var(--action)] hover:bg-[color:var(--action-hover)] text-[color:var(--action-text)] rounded-lg transition-colors font-medium"
            >
              Continue as Guest
            </button>
          ) : (
            <button
              onClick={() => {
                setShowAuthModal(false);
                setShowGuestModal(true);
              }}
              className="w-full mb-4 py-3 bg-[color:var(--action)] hover:bg-[color:var(--action-hover)] text-[color:var(--action-text)] rounded-lg transition-colors font-medium"
            >
              📱 Continue as Guest (Phone Verification)
            </button>
          )}
          
          <div className="relative mb-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">Or login with account</span>
            </div>
          </div>
          
          {/* Login Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username
              </label>
              <input
                type="text"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your username"
                disabled={authLoading}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showLoginPassword ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full pr-12 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[color:var(--focus-ring)] focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Enter your password"
                  disabled={authLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowLoginPassword(v => !v)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-600 dark:text-gray-300 hover:opacity-80"
                  aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                >
                  {showLoginPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            
            <button
              onClick={handleLogin}
              disabled={authLoading || !loginUsername.trim() || !loginPassword.trim()}
              className="w-full py-2.5 bg-[color:var(--action)] hover:bg-[color:var(--action-hover)] text-[color:var(--action-text)] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {authLoading ? 'Logging in...' : 'Login & Continue'}
            </button>
            
            {/* Divider */}
            <div className="my-4 flex items-center before:flex-1 before:border-t before:border-gray-300 dark:before:border-gray-600 after:flex-1 after:border-t after:border-gray-300 dark:after:border-gray-600">
              <p className="mx-3 mb-0 text-center text-xs font-medium text-gray-500 dark:text-gray-400">OR</p>
            </div>

            {/* Social Login Options */}
            <div className="space-y-2">
              <a
                href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'}/oauth2/authorization/google`}
                onClick={() => sessionStorage.setItem('oauth_return_url', window.location.pathname + window.location.search)}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Login with Google
              </a>

              <a
                href={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'}/oauth2/authorization/facebook`}
                onClick={() => sessionStorage.setItem('oauth_return_url', window.location.pathname + window.location.search)}
                /* basil-todo: deliberate carve-out — Facebook blue is a third-party
                   brand identity, not our palette. Tokenising it would misrepresent
                   the provider, same reason the Google button keeps its own styling. */
                className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                Login with Facebook
              </a>
            </div>
            
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              New user? <a href="/signup" className="font-medium text-[color:var(--action)] hover:underline">Create an account</a> or continue as a guest above.
            </p>
          </div>
          
          {authError && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-800 border border-red-200 dark:border-red-600 text-red-700 dark:text-red-200 rounded-lg text-sm" role="alert">
              {authError}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handlePay = async () => {
    // If not authenticated, always show auth modal first (with login option)
    // The auth modal will show appropriate guest option based on allowGuestTableOrders
    if (!isAuthenticated) {
      // Check if there's an existing session that might have expired
      const existingGuestSession = localStorage.getItem('guestSession');
      const existingUser = localStorage.getItem('user');

      if (existingGuestSession || existingUser) {
        // Show session conflict modal to let user choose
        setShowSessionConflictModal(true);
      } else {
        // No existing session, show auth modal with login + guest options
        setShowAuthModal(true);
      }
      return;
    }

    // Show confirmation dialog for authenticated users
    setShowConfirmationDialog(true);
  };

  // Handle guest name submission and proceed to order
  const handleGuestNameSubmit = () => {
    setShowGuestNameModal(false);
    setShowConfirmationDialog(true);
  };
  
  const handleConfirmOrder = async () => {
    // NEW FLOW: Close the confirmation dialog immediately and set flag
    setShowConfirmationDialog(false);
    setOrderConfirmed(true);
    setLoading(true);
    setError(null);
    
    if (process.env.NODE_ENV === 'development') {
      console.log("=== PAYMENT PROCESS STARTED ===");
    }
    
    // Process order asynchronously
    processOrderAsync();
  };

  // NEW: Async function to process the order
  const processOrderAsync = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log("Preparing order payload...");
      }
      
      const payloadItems = cartState.items.map(ci => ({
        menuItem: { id: ci.menuItem.id },
        quantity: ci.quantity,
        price: ci.price,
        portionId: ci.portionId, // Add portion ID for backend
        comment: ci.comment || '', // Only use item-level comments, NOT order-level specialInstructions
        toppings: ci.toppings?.map(tid => ({ id: tid })),
        requestedSpiceLevel: ci.requestedSpiceLevel // Add spice level for backend
      }));

      if (process.env.NODE_ENV === 'development') {
        console.log("Order payload:", { payloadItems, deliveryType, user: user?.id, guestSession: guestSession?.phoneNumber, qrCode });
      }

      let response;
      const isValidModification = modificationState.isModifying && 
                                  modificationState.canModify && 
                                  modificationState.originalOrderId;
      
      if (process.env.NODE_ENV === 'development') {
        console.log("=== ORDER MODIFICATION DECISION ===");
        console.log("modificationState:", modificationState);
        console.log("isValidModification:", isValidModification);
        console.log("Will update existing order:", isValidModification);
        console.log("Will create new order:", !isValidModification);
        console.log("====================================");
      }
      
      // Check for existing session before placing order
      if (isGuest && guestSession) {
        // Validate guest session is still active
        const isValid = await validateSessionWithBackend();
        if (!isValid) {
          console.log("Guest session expired, showing session conflict modal");
          setShowSessionConflictModal(true);
          setLoading(false);
          setOrderConfirmed(false);
          return;
        }
      }
      
      if (isValidModification) {
        if (process.env.NODE_ENV === 'development') {
          console.log("🔄 UPDATING EXISTING ORDER:", modificationState.originalOrderId);
          console.log("Order data being sent:", { items: payloadItems, deliveryType });
        }
        
        // Check if there's a payment difference before proceeding
        const originalOrder = await api.get(`/orders/${modificationState.originalOrderId}`);
        const originalTotal = originalOrder.data.total;
        const newTotal = total;
        
        if (Math.abs(newTotal - originalTotal) > 0.01) {
          // There's a payment difference, show payment modal
          setShowPaymentModal(true);
          setLoading(false);
          setOrderConfirmed(false);
          return;
        }
        
        response = await api.put(`/orders/${modificationState.originalOrderId}`, {
          items: payloadItems,
          deliveryType,
        });
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log("🆕 CREATING NEW ORDER");
          console.log("Reason for new order:", {
            isModifying: modificationState.isModifying,
            canModify: modificationState.canModify,
            hasOriginalOrderId: !!modificationState.originalOrderId
          });
        }
        
        // Prepare order data for both authenticated and guest users
        const orderQrCode = cartState.qrCode || qrCode || localStorage.getItem('qrCode');
        
        if (!orderQrCode) {
          console.error("No QR code available for order creation");
          setError("QR code is required to place an order. Please scan a QR code first.");
          setLoading(false);
          setOrderConfirmed(false);
          return;
        }
        
        interface OrderData {
          qrCode: string;
          items: typeof payloadItems;
          deliveryType: typeof deliveryType;
          orderNotes?: string; // Order-level special instructions
          allergyNote?: string; // Allergy declaration — highlighted on the KOT
          guestInfo?: {
            name: string;
            phone: string | null; // null for anonymous table orders
            email: string | null;
          };
          user?: {
            id: number;
          };
        }

        const orderData: OrderData = {
          qrCode: orderQrCode,
          items: payloadItems,
          deliveryType,
          orderNotes: specialInstructions || undefined, // Pass as order-level notes
          allergyNote: allergyNote.trim() || undefined,
        };

        if (isGuest && guestSession) {
          // For guest users, include guest information from session
          orderData.guestInfo = {
            name: guestSession.name || "Guest User",
            phone: guestSession.phoneNumber,
            email: null // No email in basic session
          };

          // Log guest order data for debugging
          if (process.env.NODE_ENV === 'development') {
            console.log("Guest order data:", orderData);
            console.log("Guest session:", guestSession);
            console.log("Auth headers will include:", {
              'X-Guest-Session-Token': guestSession.sessionToken,
              'Authorization': token ? `Bearer ${token}` : 'Not set'
            });
          }
        } else if (user) {
          // For authenticated users, include user ID
          orderData.user = { id: user.id };
        } else if ((isTableOrder === true || (isTableOrder === null && tableInfo !== null)) && allowGuestTableOrders) {
          // For anonymous table orders (vendor allows guest table orders without login)
          // Consider it a table order if isTableOrder is true OR if we have tableInfo
          // Include guest name if provided
          if (guestName.trim()) {
            orderData.guestInfo = {
              name: guestName.trim(),
              phone: null,
              email: null
            };
          }

          // Use the dedicated anonymous table order endpoint
          if (process.env.NODE_ENV === 'development') {
            console.log("Anonymous table order data:", orderData);
            console.log("Table info:", tableInfo);
            console.log("Guest name:", guestName);
          }

          // Use the anonymous table order endpoint
          response = await api.post('/orders/table', orderData);

          // Early return to skip the regular order flow
          if (process.env.NODE_ENV === 'development') {
            console.log("Anonymous table order response:", response);
          }
        }

        // Only make regular /orders call if we haven't already created the order
        if (!response) {
          response = await api.post('/orders', orderData);
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log("Order API response:", response);
        console.log("Response data:", response.data);
        console.log("Response status:", response.status);
      }
      
      // Check if response contains error
      if (!response.data || response.status >= 400) {
        throw new Error("Failed to create order. Server returned an error.");
      }
      
      const newOrderId = response.data.id;
      const orderStatus = response.data.status;
      
      if (process.env.NODE_ENV === 'development') {
        console.log("Order processed with ID:", newOrderId, "Status:", orderStatus);
      }
      
      // Validate that we have a valid order ID
      if (!newOrderId) {
        console.error("Response data structure:", response.data);
        throw new Error("Order was created but no order ID was returned. Response: " + JSON.stringify(response.data));
      }
      
      // CRITICAL FIX: Always clear cart and pending order ID after successful order placement
      // This prevents the cart from persisting and showing up in history
      clearCartAfterOrderPlacement(newOrderId.toString());
      setPendingOrderId(null);
      setLoading(false);
      setProcessingOrderId(newOrderId); // Set the order ID for redirection
      
      // NOTE: Navigation will be handled by useEffect when processingOrderId is set
      
    } catch (err) {
      console.error("Order processing failed:", err);
      
      let errorMessage = "Failed to process order. Please try again.";
      
      // Check if we received an HTML response (authentication redirect)
      if (err.response?.data?.isHtmlResponse || err.response?.status === 401) {
        console.log("Authentication required - showing auth modal");
        setError(null);
        setLoading(false);
        setOrderConfirmed(false);
        
        // Clear any stale authentication state
        if (isGuest) {
          // For guest users, show the guest verification modal again
          setShowGuestModal(true);
        } else {
          // For regular users, show the auth modal
          setShowAuthModal(true);
        }
        return;
      }
      
      // Handle specific error cases
      if (err.response?.status === 400) {
        // Business logic error - use userMessage if available, otherwise use message
        const businessErrorMsg = err.response.data?.userMessage || err.response.data?.message || "Invalid order data. Please check your items and try again.";
        errorMessage = businessErrorMsg;

        // Show toast notification for business errors (like unavailable items)
        showToast({
          message: businessErrorMsg,
          kind: 'error',
          durationMs: 6000
        });
      } else if (err.response?.status === 404) {
        errorMessage = "Order not found or no longer available for modification.";
        // Clear modification state if order no longer exists
        setModificationState(prev => ({ ...prev, canModify: false, warningMessage: errorMessage }));
      } else if (err.response?.status === 403) {
        errorMessage = "You don't have permission to modify this order.";
      } else if (err.response?.status === 500) {
        // Log the full error details for 500 errors
        console.error("Server error details:", {
          status: err.response?.status,
          data: err.response?.data,
          headers: err.response?.headers,
          config: err.config
        });

        // Try to extract meaningful error message from response
        // The API interceptor has already processed AccessDeniedException messages
        const serverMessage = err.response?.data?.message || err.response?.data?.error;
        if (serverMessage && typeof serverMessage === 'string') {
          // Use the actual error message from server (already processed by API interceptor)
          errorMessage = serverMessage;
        } else {
          errorMessage = "Server error occurred. Please try again or contact support if the issue persists.";
        }
      }
      
      setError(errorMessage);
      setLoading(false);
      setOrderConfirmed(false);
    }
  };

  // Effect to handle redirection after order confirmation
  useEffect(() => {
    if (orderConfirmed && processingOrderId) {
      const newOrderId = processingOrderId;
      const isValidModification = modificationState.isModifying && 
                                  modificationState.canModify && 
                                  modificationState.originalOrderId;
      
      const successMessage = isValidModification 
        ? 'Order updated successfully!' 
        : 'Order placed successfully!';
      
      // Add a small delay before navigation to ensure backend transaction commits
      setTimeout(() => {
        navigate(`/order-success/${newOrderId}`, { 
          replace: true,
          state: { 
            isModification: isValidModification,
            message: successMessage,
            isTableOrder: isTableOrder // Pass the actual table order status
          }
        });
      }, 100); // 100ms delay
    }
  }, [orderConfirmed, processingOrderId, modificationState.isModifying, modificationState.canModify, modificationState.originalOrderId, navigate]);

  // Empty cart state - but show loading if this is a modification that might still be loading
  if (cartState.items.length === 0) {
    // If this is a modification scenario and we just landed on the page, show loading briefly
    if (isModification && orderId) {
      return (
        <div className="min-h-screen bg-[var(--color-background-default)]">
          <div className="max-w-md mx-auto pt-16 px-4">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-6 bg-[var(--color-background-surface)] rounded-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[var(--color-brand-purple)]"></div>
              </div>
              <h2 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">Loading order...</h2>
              <p className="text-[var(--color-text-secondary)] mb-8">
                Loading your order items for modification
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen bg-[var(--color-background-default)]">
        <div className="max-w-md mx-auto pt-16 px-4">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-6 bg-[var(--color-background-surface)] rounded-full flex items-center justify-center">
              <svg className="w-16 h-16 text-[var(--color-text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5-6m0 0h15M17 21a2 2 0 100-4 2 2 0 000 4zM9 21a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">Your cart is empty</h2>
            <p className="text-[var(--color-text-secondary)] mb-8">
              {modificationState.isModifying 
                ? "No items to modify. Add some items to update your order."
                : "Add some delicious items to get started!"
              }
            </p>
            <button
              onClick={() => {
                const targetVendorId = cartState.vendorId || vendorId || localStorage.getItem('vendorId');
                const targetQrCode = cartState.qrCode || qrCode || localStorage.getItem('qrCode');
                
                if (targetVendorId) {
                  navigate(`/menu/${targetVendorId}`);
                } else if (targetQrCode) {
                  navigate(`/menu?qr=${targetQrCode}`);
                } else {
                  navigate('/qr');
                }
              }}
              className="w-full py-3.5 px-6 rounded-xl font-semibold transition-all flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(191, 219, 254, 0.3)',
                color: 'var(--text-link)',
                border: '2px solid rgba(96, 165, 250, 0.4)',
                boxShadow: '0 1px 3px rgba(37, 99, 235, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(191, 219, 254, 0.5)';
                e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(191, 219, 254, 0.3)';
                e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.4)';
              }}
            >
              Browse Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-[var(--color-background-default)]"
      style={{
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: isMobile ? 'max(80px, calc(80px + env(safe-area-inset-bottom, 0px)))' : '0'
      }}
    >
      {/* Header */}
      <div 
        className="sticky z-10 bg-[var(--color-background-surface)] shadow-sm border-b border-[var(--color-border-default)]"
        style={{
          top: 0,
          paddingTop: 'max(12px, calc(12px + env(safe-area-inset-top, 0px)))',
          paddingBottom: '12px',
          paddingLeft: 'max(16px, calc(16px + env(safe-area-inset-left, 0px)))',
          paddingRight: 'max(16px, calc(16px + env(safe-area-inset-right, 0px)))'
        }}
      >
        <div className="max-w-6xl mx-auto"
          style={{
            paddingLeft: '0',
            paddingRight: '0'
          }}
        >
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                // Prefer cart/vendor context to decide safe back target
                const targetVendorId = cartState.vendorId || vendorId || localStorage.getItem('vendorId');
                const targetQrCode = cartState.qrCode || qrCode || localStorage.getItem('qrCode');
                if (targetVendorId) {
                  navigate(`/menu/${targetVendorId}`);
                } else if (targetQrCode) {
                  navigate(`/menu?qr=${targetQrCode}`);
                } else {
                  navigate('/qr');
                }
              }}
              className="p-1.5 -ml-1.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-[var(--color-text-primary)]" style={{ fontFamily: 'var(--font-family-heading)' }}>
              {modificationState.isModifying ? 'Modify Order' : 'Checkout'}
            </h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      {/* Table Information Display */}
      {isTableOrder && tableInfo?.name && (
        <div className="bg-[var(--color-background-surface)] border-b border-[var(--color-border-default)]">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5 text-[var(--color-brand-purple)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="text-[var(--color-text-primary)] font-medium">
                Table {tableInfo.name}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Loyalty Banner */}
      {loyaltyProgress.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pt-4">
          {loyaltyProgress.map((reward) => (
            <div
              key={reward.id}
              onClick={() => setShowLoyaltyDetails(!showLoyaltyDetails)}
              className="cursor-pointer mb-3 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md"
              style={{
                background: reward.rewardAvailable
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                  : `linear-gradient(135deg, ${reward.badgeColor || '#8B5CF6'} 0%, ${reward.badgeColor ? reward.badgeColor + 'CC' : '#7C3AED'} 100%)`
              }}
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      {reward.rewardAvailable ? (
                        <span className="text-xl">🎁</span>
                      ) : (
                        <span className="text-xl">
                          {reward.programType === 'VISIT_BASED' && '🎯'}
                          {reward.programType === 'SPEND_BASED' && '💰'}
                          {reward.programType === 'POINTS_BASED' && '⭐'}
                          {reward.programType === 'ITEM_BASED' && '🍽️'}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">
                        {reward.rewardAvailable ? 'Reward Available!' : reward.programName}
                      </h3>
                      <p className="text-white/80 text-xs mt-0.5">
                        {reward.rewardAvailable
                          ? (reward.rewardDescription || `${CURRENCY.symbol}${reward.rewardValue} off your order`)
                          : reward.progressDisplay}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!reward.rewardAvailable && (
                      <div className="text-right">
                        <span className="text-white font-bold text-lg">{reward.progressPercentage}%</span>
                      </div>
                    )}
                    <svg
                      className={`w-5 h-5 text-white/80 transition-transform duration-300 ${showLoyaltyDetails ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Progress bar for non-available rewards */}
                {!reward.rewardAvailable && (
                  <div className="mt-3">
                    <LinearProgress
                      variant="determinate"
                      value={reward.progressPercentage}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          backgroundColor: 'white',
                        },
                      }}
                    />
                  </div>
                )}

                {/* Expandable details */}
                {showLoyaltyDetails && (
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <div className="space-y-2 text-sm text-white/90">
                      <p><strong>Program:</strong> {reward.programName}</p>
                      <p><strong>How it works:</strong> {reward.programSummary}</p>
                      {reward.rewardAvailable ? (
                        <>
                          <p><strong>Your reward:</strong> {reward.rewardDescription || `${CURRENCY.symbol}${reward.rewardValue} off`}</p>
                          {reward.rewardExpiresAt && (
                            <p className="text-white/70 text-xs">
                              Expires: {new Date(reward.rewardExpiresAt).toLocaleDateString()}
                            </p>
                          )}
                          <p className="mt-2 text-xs text-white/70">
                            Complete this order to auto-apply your reward!
                          </p>
                        </>
                      ) : (
                        <>
                          <p>
                            <strong>Progress:</strong>{' '}
                            {reward.programType === 'VISIT_BASED' && `${reward.qualifyingVisits} qualifying visits`}
                            {reward.programType === 'SPEND_BASED' && `${CURRENCY.symbol}${reward.qualifyingSpend.toFixed(0)} spent`}
                            {reward.programType === 'POINTS_BASED' && `${reward.pointsBalance} points`}
                            {reward.programType === 'ITEM_BASED' && `${reward.itemPurchaseCount} items purchased`}
                          </p>
                          <p>
                            <strong>To unlock:</strong>{' '}
                            {reward.programType === 'VISIT_BASED' && `${reward.remainingToReward} more order${reward.remainingToReward > 1 ? 's' : ''}`}
                            {reward.programType === 'SPEND_BASED' && `Spend ${CURRENCY.symbol}${reward.remainingToReward.toFixed(0)} more`}
                            {reward.programType === 'POINTS_BASED' && `Earn ${reward.remainingToReward} more points`}
                            {reward.programType === 'ITEM_BASED' && `Buy ${reward.remainingToReward} more item${reward.remainingToReward > 1 ? 's' : ''}`}
                          </p>
                        </>
                      )}
                      {reward.cyclesCompleted > 0 && (
                        <p className="text-xs text-white/70">
                          You've redeemed this reward {reward.cyclesCompleted} time{reward.cyclesCompleted > 1 ? 's' : ''} before!
                        </p>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/rewards');
                      }}
                      className="mt-3 w-full py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      View All Rewards
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order Modification Warning/Info */}
      {modificationState.isModifying && (
        <div className="max-w-6xl mx-auto px-4 py-4">
          {modificationState.canModify ? (
            <div className="bg-[color:var(--status-info-bg)] border border-[color:var(--status-info-border)] rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-[color:var(--status-info)] mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-[color:var(--status-info-text)]">Modifying Order #{modificationState.originalOrderId}</h3>
                  <p className="text-sm text-[color:var(--status-info-text)] mt-1">
                    You're updating a pending order. Changes will be saved automatically as you make them.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">Cannot Modify Order</h3>
                  <p className="text-sm text-yellow-700 mt-1">{modificationState.warningMessage}</p>
                  <button
                    onClick={handleStartNewOrder}
                    className="mt-2 text-sm font-medium text-yellow-800 hover:text-yellow-900 underline"
                  >
                    Start New Order Instead
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}



      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Left Column - Order Items */}
          <div className="lg:col-span-2">
            <div className={`${isModernTheme ? 'bg-white rounded-2xl shadow-sm' : 'bg-[var(--color-background-surface)] rounded-lg'} p-6 mb-6`}>
              <h2 className={`${isModernTheme ? 'text-xl font-bold text-gray-900' : 'text-lg font-semibold text-[var(--color-text-primary)]'} mb-4`}>Your Order</h2>
              <div className="space-y-4">
                {(() => {
                  // Group cart items by menu item ID
                  const groupedItems = cartState.items.reduce((groups, ci) => {
                    const key = ci.menuItem.id;
                    if (!groups[key]) {
                      groups[key] = [];
                    }
                    groups[key].push(ci);
                    return groups;
                  }, {} as { [key: number]: typeof cartState.items });

                  return Object.entries(groupedItems).map(([menuItemId, cartItems]) => {
                    const itemDef = menuCategories.flatMap(cat => cat.items).find(it => it.id === Number(menuItemId));
                    const totalQuantity = cartItems.reduce((sum, ci) => sum + ci.quantity, 0);
                    const totalPrice = cartItems.reduce((sum, ci) => sum + (ci.price * ci.quantity), 0);
                    const hasMultiplePortions = cartItems.length > 1;
                    const itemName = itemDef?.name || cartItems[0]?.menuItem?.name || `Item #${menuItemId}`;
                    const itemStyle = isModernTheme ? getItemStyle(itemName) : null;

                    return (
                      <div key={menuItemId} className={`pb-4 ${isModernTheme ? 'border-b border-gray-100' : 'border-b border-[var(--color-border-default)]'} last:border-0 last:pb-0`}>
                        {/* Main Item Header */}
                        <div className="flex items-start space-x-4">
                          {/* Item Image/Emoji */}
                          {itemDef?.imageUrl ? (
                            <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 shadow-sm">
                              <MenuItemImage imageUrl={itemDef.imageUrl} itemName={itemName} size="thumbnail" />
                            </div>
                          ) : (
                            <div
                              className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${isModernTheme ? 'shadow-sm' : ''}`}
                              style={isModernTheme && itemStyle ? {
                                background: itemStyle.gradient,
                                border: `1px solid ${itemStyle.color}20`
                              } : {
                                backgroundColor: 'var(--color-background-default)'
                              }}
                            >
                              <span className={`${isModernTheme ? 'text-4xl' : 'text-2xl'}`}>
                                {isModernTheme && itemStyle ? itemStyle.emoji : '🍽️'}
                              </span>
                            </div>
                          )}
                          
                          {/* Item Details */}
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h3 className={`font-semibold ${isModernTheme ? 'text-gray-900 text-base' : 'text-[var(--color-text-primary)]'}`}>
                                  <DietaryDot type={itemDef?.dietaryType || (cartItems[0]?.menuItem as { dietaryType?: 'VEG' | 'NON_VEG' | 'VEGAN' })?.dietaryType} />
                                                                    {itemName}
                                </h3>
                                {itemDef?.description && (
                                  <p className="text-sm mt-1" style={{ color: theme.gray[500] || '#64748b', lineHeight: '1.5' }}>
                                    {itemDef.description}
                                  </p>
                                )}
                                {!hasMultiplePortions && cartItems[0].portionName && (
                                  <div className="mt-2">
                                    <Chip
                                      label={cartItems[0].portionName}
                                      size="small"
                                      sx={isModernTheme && itemStyle ? {
                                        height: 28,
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        backgroundColor: `${itemStyle.color}15`,
                                        color: itemStyle.color,
                                        border: `1px solid ${itemStyle.color}40`,
                                        borderRadius: '8px'
                                      } : {
                                        height: 28,
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        backgroundColor: theme.components?.badge?.info?.bg || 'rgba(241, 245, 249, 1)',
                                        color: theme.components?.badge?.info?.text || '#475569',
                                        border: `1px solid ${theme.components.card.border || 'rgba(203, 213, 225, 0.6)'}`,
                                        borderRadius: '8px'
                                      }}
                                    />
                                  </div>
                                )}
                                {!hasMultiplePortions && (
                                  <p className="text-sm font-semibold mt-2" style={{ color: isModernTheme && itemStyle ? itemStyle.color : (theme.primary || '#2563eb') }}>
                                    {CURRENCY.symbol}{cartItems[0].price.toFixed(2)} each
                                  </p>
                                )}

                                {/* Spice Level Display */}
                                {cartItems[0].requestedSpiceLevel && cartItems[0].requestedSpiceLevel !== 'NONE' && (
                                  <div className="mt-2">
                                    <SpiceLevelBadge spiceLevel={cartItems[0].requestedSpiceLevel} />
                                  </div>
                                )}

                                {/* Toppings Display */}
                                {cartItems[0].selectedToppings && cartItems[0].selectedToppings.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    <p className="text-xs font-medium text-[var(--color-text-secondary)] uppercase">Toppings:</p>
                                    {(() => {
                                      const sortedToppings = [...cartItems[0].selectedToppings].sort((a, b) => b.price - a.price);
                                      const freeCount = cartItems[0].freeToppingsCount || 0;

                                      return sortedToppings.map((topping, idx) => {
                                        const isFree = idx < freeCount;
                                        return (
                                          <div key={topping.id} className="flex items-center justify-between text-xs">
                                            <span className="text-[var(--color-text-secondary)]">
                                              • {topping.name}
                                              {isFree && <span className="ml-1 text-green-600 font-medium">(Free)</span>}
                                            </span>
                                            <span className={isFree ? "text-green-600 line-through" : "text-[var(--color-text-primary)]"}>
                                              +{CURRENCY.symbol}{topping.price.toFixed(2)}
                                            </span>
                                          </div>
                                        );
                                      });
                                    })()}
                                  </div>
                                )}
                              </div>

                              {/* Remove Button */}
                              <button
                                onClick={() => removeFromCart(Number(menuItemId))}
                                disabled={modificationState.isModifying && !modificationState.canModify}
                                className="p-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ml-3"
                                style={{
                                  backgroundColor: 'rgba(254, 226, 226, 0.5)',
                                  border: '1px solid rgba(252, 165, 165, 0.4)',
                                  color: 'rgba(185, 28, 28, 0.85)'
                                }}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                            
                                                         {/* Portion Details - Show when multiple portions */}
                             {hasMultiplePortions && (
                               <div className="mt-3">
                                 {/* Desktop Table Layout */}
                                 <div className="hidden sm:block">
                                   <div className="grid grid-cols-12 gap-2 items-center py-2 px-4 bg-gray-50 rounded-t-lg text-xs font-medium text-gray-600 uppercase tracking-wide">
                                     <div className="col-span-4">Portion</div>
                                     <div className="col-span-3 text-center">Price</div>
                                     <div className="col-span-3 text-center">Quantity</div>
                                     <div className="col-span-2 text-right">Subtotal</div>
                                   </div>
                                   {cartItems.map((ci, index) => (
                                     <div key={index} className={`grid grid-cols-12 gap-2 items-center py-3 px-4 ${index < cartItems.length - 1 ? 'border-b border-gray-100' : ''} ${index === 0 ? 'bg-[var(--color-background-default)] rounded-none' : 'bg-[var(--color-background-default)]'} ${index === cartItems.length - 1 ? 'rounded-b-lg' : ''}`}>
                                       {/* Portion Name */}
                                       <div className="col-span-4">
                                         <span className="text-sm font-medium text-[var(--color-text-primary)]">
                                           {ci.portionName || 'Standard'}
                                         </span>
                                       </div>
                                       
                                       {/* Price */}
                                       <div className="col-span-3 text-center">
                                         <span className="text-sm font-medium text-[var(--color-brand-purple)]">
                                           {CURRENCY.symbol}{ci.price.toFixed(2)}
                                         </span>
                                       </div>
                                       
                                       {/* Quantity Controls */}
                                       <div className="col-span-3 flex items-center justify-center space-x-2">
                                         <button
                                           onClick={() => {
                                             handlePortionQuantityChange(ci, -1);
                                           }}
                                           disabled={modificationState.isModifying && !modificationState.canModify}
                                           className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[var(--color-text-secondary)] hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                         >
                                           <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                           </svg>
                                         </button>
                                         <span className="font-semibold text-[var(--color-text-primary)] min-w-[1.5rem] text-center text-sm">
                                           {ci.quantity}
                                         </span>
                                         <button
                                           onClick={() => {
                                             handlePortionQuantityChange(ci, 1);
                                           }}
                                           disabled={modificationState.isModifying && !modificationState.canModify}
                                           className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[var(--color-text-secondary)] hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                         >
                                           <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                           </svg>
                                         </button>
                                       </div>
                                       
                                       {/* Subtotal */}
                                       <div className="col-span-2 text-right">
                                         <span className="font-semibold text-[var(--color-text-primary)] text-sm">
                                           {CURRENCY.symbol}{(ci.price * ci.quantity).toFixed(2)}
                                         </span>
                                       </div>
                                     </div>
                                   ))}
                                 </div>

                                {/* Mobile Card Layout */}
                                <div 
                                  className="block sm:hidden space-y-3"
                                  style={{
                                    marginLeft: 'calc(-5rem - 1rem)',
                                    marginRight: '0',
                                    paddingLeft: '1rem',
                                    paddingRight: '1rem'
                                  }}
                                >
                                  {cartItems.map((ci, index) => (
                                    <div 
                                      key={index} 
                                      className="rounded-xl p-4 border"
                                      style={isModernTheme && itemStyle ? {
                                        backgroundColor: 'white',
                                        borderColor: `${itemStyle.color}30`,
                                        boxShadow: `0 2px 8px ${itemStyle.color}10`,
                                        marginLeft: '0',
                                        marginRight: '0'
                                      } : {
                                        backgroundColor: 'rgba(248, 250, 252, 1)',
                                        borderColor: 'rgba(226, 232, 240, 0.8)',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
                                        marginLeft: '0',
                                        marginRight: '0'
                                      }}
                                    >
                                      {/* Portion Header */}
                                      <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1">
                                          <div className="flex items-center flex-wrap gap-2 mb-2">
                                            <Chip
                                              label={ci.portionName || 'Standard'}
                                              size="small"
                                              sx={isModernTheme && itemStyle ? {
                                                height: 28,
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                backgroundColor: `${itemStyle.color}15`,
                                                color: itemStyle.color,
                                                border: `1px solid ${itemStyle.color}40`,
                                                borderRadius: '8px'
                                              } : {
                                                height: 28,
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                backgroundColor: 'var(--bg-surface)',
                                                color: 'var(--text-secondary)',
                                                border: '1px solid rgba(203, 213, 225, 0.6)',
                                                borderRadius: '8px'
                                              }}
                                            />
                                            {ci.requestedSpiceLevel && ci.requestedSpiceLevel !== 'NONE' && (
                                              <SpiceLevelBadge spiceLevel={ci.requestedSpiceLevel} size="small" />
                                            )}
                                          </div>
                                          <div 
                                            className="text-xs font-semibold"
                                            style={{ color: isModernTheme && itemStyle ? itemStyle.color : 'var(--text-link)' }}
                                          >
                                            {CURRENCY.symbol}{ci.price.toFixed(2)} each
                                          </div>
                                        </div>
                                        <div className="text-right ml-3">
                                          <div 
                                            className="font-bold text-base"
                                            style={{ color: isModernTheme && itemStyle ? itemStyle.color : 'var(--text-primary)' }}
                                          >
                                            {CURRENCY.symbol}{(ci.price * ci.quantity).toFixed(2)}
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Quantity Controls */}
                                      <div 
                                        className={`flex items-center justify-center pt-3 ${isModernTheme ? '' : 'space-x-4'}`}
                                        style={{
                                          borderTop: isModernTheme && itemStyle ? `1px solid ${itemStyle.color}20` : '1px solid rgba(226, 232, 240, 0.6)'
                                        }}
                                      >
                                        <div className={`flex items-center ${isModernTheme ? 'rounded-xl overflow-hidden' : 'space-x-4'}`} style={isModernTheme && itemStyle ? { border: `2px solid ${itemStyle.color}` } : {}}>
                                          <button
                                            onClick={() => {
                                              handlePortionQuantityChange(ci, -1);
                                            }}
                                            disabled={modificationState.isModifying && !modificationState.canModify}
                                            className={`${isModernTheme ? 'w-10 h-10' : 'w-10 h-10 rounded-full'} flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                                            style={isModernTheme && itemStyle ? {
                                              backgroundColor: `${itemStyle.color}20`,
                                              color: itemStyle.color
                                            } : {
                                              backgroundColor: 'var(--bg-sunken)',
                                              color: 'var(--text-tertiary)',
                                              border: '1px solid rgba(203, 213, 225, 0.5)'
                                            }}
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                            </svg>
                                          </button>
                                          <span 
                                            className={`font-bold min-w-[2.5rem] text-center text-lg ${isModernTheme ? 'bg-white px-2' : ''}`}
                                            style={{ color: isModernTheme && itemStyle ? itemStyle.color : 'var(--text-primary)' }}
                                          >
                                            {ci.quantity}
                                          </span>
                                          <button
                                            onClick={() => {
                                              handlePortionQuantityChange(ci, 1);
                                            }}
                                            disabled={modificationState.isModifying && !modificationState.canModify}
                                            className={`${isModernTheme ? 'w-10 h-10' : 'w-10 h-10 rounded-full'} flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                                            style={isModernTheme && itemStyle ? {
                                              backgroundColor: itemStyle.color,
                                              color: 'white'
                                            } : {
                                              backgroundColor: 'rgba(191, 219, 254, 0.3)',
                                              color: 'var(--text-link)',
                                              border: '1px solid rgba(96, 165, 250, 0.4)'
                                            }}
                                          >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                               </div>
                             )}
                            
                            {/* Single Portion Quantity Controls */}
                            {!hasMultiplePortions && (
                              <div className="flex items-center justify-between mt-3">
                                <div className={`flex items-center ${isModernTheme ? 'rounded-xl overflow-hidden' : 'space-x-3'}`} style={isModernTheme && itemStyle ? { border: `2px solid ${itemStyle.color}` } : {}}>
                                  <button
                                    onClick={() => handleQuantityChange(cartItems[0].menuItem.id, cartItems[0].quantity - 1)}
                                    disabled={modificationState.isModifying && !modificationState.canModify}
                                    className={`${isModernTheme ? 'w-9 h-9' : 'w-8 h-8 rounded-full'} flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                                    style={isModernTheme && itemStyle ? {
                                      backgroundColor: `${itemStyle.color}20`,
                                      color: itemStyle.color
                                    } : {
                                      backgroundColor: 'var(--color-background-default)',
                                      color: 'var(--color-text-secondary)'
                                    }}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                    </svg>
                                  </button>
                                  <span className={`font-semibold min-w-[2rem] text-center ${isModernTheme ? 'bg-white px-2' : ''}`} style={{ color: isModernTheme && itemStyle ? itemStyle.color : 'var(--color-text-primary)' }}>
                                    {cartItems[0].quantity}
                                  </span>
                                  <button
                                    onClick={() => handleQuantityChange(cartItems[0].menuItem.id, cartItems[0].quantity + 1)}
                                    disabled={modificationState.isModifying && !modificationState.canModify}
                                    className={`${isModernTheme ? 'w-9 h-9' : 'w-8 h-8 rounded-full'} flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                                    style={isModernTheme && itemStyle ? {
                                      backgroundColor: itemStyle.color,
                                      color: 'white'
                                    } : {
                                      backgroundColor: 'var(--color-background-default)',
                                      color: 'var(--color-text-secondary)'
                                    }}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                  </button>
                                </div>
                                <span className="font-bold" style={{ color: isModernTheme && itemStyle ? itemStyle.color : 'var(--color-text-primary)' }}>
                                  {CURRENCY.symbol}{(cartItems[0].price * cartItems[0].quantity).toFixed(2)}
                                </span>
                              </div>
                            )}
                            
                            {/* Total for this item when multiple portions */}
                            {hasMultiplePortions && (
                              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--color-border-default)]">
                                <span className="font-semibold text-[var(--color-text-primary)]">
                                  Total ({totalQuantity} items)
                                </span>
                                <span className="font-semibold text-[var(--color-text-primary)]">
                                  {CURRENCY.symbol}{totalPrice.toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
              
              {/* Add More Items Button */}
              <button
                onClick={() => {
                  const targetVendorId = cartState.vendorId || vendorId || localStorage.getItem('vendorId');
                  const targetQrCode = cartState.qrCode || qrCode || localStorage.getItem('qrCode');
                  
                  if (targetVendorId) {
                    navigate(`/menu/${targetVendorId}`);
                  } else if (targetQrCode) {
                    navigate(`/menu?qr=${targetQrCode}`);
                  } else {
                    navigate('/qr');
                  }
                }}
                disabled={modificationState.isModifying && !modificationState.canModify}
                className="w-full mt-4 py-3 px-4 border-2 border-dashed border-[var(--color-border-default)] rounded-lg text-[var(--color-text-secondary)] hover:border-[var(--color-brand-purple)] hover:text-[var(--color-brand-purple)] transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="font-medium">Add More Items</span>
              </button>
            </div>

            {/* Special Instructions - Desktop Only */}
            <div className="bg-[var(--color-background-surface)] rounded-lg p-6 mb-6 hidden lg:block">
              <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
                Special Instructions
              </label>
              <textarea
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                placeholder="Any special instructions? (e.g. No onions, extra spicy)"
                disabled={modificationState.isModifying && !modificationState.canModify}
                className="w-full px-4 py-3 bg-[var(--color-background-default)] border border-[var(--color-border-default)] rounded-lg text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] focus:ring-2 focus:ring-[var(--color-brand-purple)] focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                rows={3}
              />
              <label className="block text-sm font-medium text-[var(--color-text-primary)] mt-4 mb-2">
                Allergies <span className="font-normal text-[var(--color-text-secondary)]">(optional — the kitchen will be alerted)</span>
              </label>
              <input
                type="text"
                value={allergyNote}
                onChange={(e) => setAllergyNote(e.target.value)}
                placeholder="e.g. Peanut allergy — please avoid cross-contact"
                maxLength={500}
                disabled={modificationState.isModifying && !modificationState.canModify}
                className="w-full px-4 py-3 bg-[var(--color-background-default)] border border-[var(--color-border-default)] rounded-lg text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] focus:ring-2 focus:ring-[var(--color-brand-purple)] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[var(--color-background-surface)] rounded-lg p-6 sticky top-6">
              {/* Delivery Type */}
              <h3 className="text-sm font-medium text-[var(--color-text-primary)] mb-3">Delivery Option</h3>
              <div className="space-y-2 mb-6">
                {/* Show "Deliver to Table" option only for table orders or when it's null (loading) */}
                {(isTableOrder === true || isTableOrder === null) && (
                  <label className="flex items-center p-3 bg-[var(--color-background-default)] rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="deliveryType"
                      value="DELIVER_TO_TABLE"
                      checked={deliveryType === 'DELIVER_TO_TABLE'}
                      onChange={() => setDeliveryType('DELIVER_TO_TABLE')}
                      disabled={modificationState.isModifying && !modificationState.canModify}
                      className="w-4 h-4 text-[var(--color-brand-purple)] border-gray-300 focus:ring-[var(--color-brand-purple)] disabled:opacity-50"
                    />
                    <div className="ml-3">
                      <p className="font-medium text-[var(--color-text-primary)] text-sm">
                        Deliver to Table{tableInfo?.name ? ` - ${tableInfo.name}` : ''}
                      </p>
                      <p className="text-xs text-[var(--color-text-secondary)]">We'll bring it to you</p>
                    </div>
                  </label>
                )}
                
                {/* Show "Collect at Counter" option only for counter orders or when it's null (loading) */}
                {(isTableOrder === false || isTableOrder === null) && (
                  <label className="flex items-center p-3 bg-[var(--color-background-default)] rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="deliveryType"
                      value="COLLECT"
                      checked={deliveryType === 'COLLECT'}
                      onChange={() => setDeliveryType('COLLECT')}
                      disabled={modificationState.isModifying && !modificationState.canModify}
                      className="w-4 h-4 text-[var(--color-brand-purple)] border-gray-300 focus:ring-[var(--color-brand-purple)] disabled:opacity-50"
                    />
                    <div className="ml-3">
                      <p className="font-medium text-[var(--color-text-primary)] text-sm">Collect at Counter</p>
                      <p className="text-xs text-[var(--color-text-secondary)]">Pick up when ready</p>
                    </div>
                  </label>
                )}
              </div>

              {/* Special Instructions - Mobile Only */}
              <div className="mb-6 lg:hidden">
                <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
                  Special Instructions
                </label>
                <textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Any special instructions?"
                  disabled={modificationState.isModifying && !modificationState.canModify}
                  className="w-full px-3 py-2 bg-[var(--color-background-default)] border border-[var(--color-border-default)] rounded-lg text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] focus:ring-2 focus:ring-[var(--color-brand-purple)] focus:border-transparent resize-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  rows={2}
                />
                <label className="block text-sm font-medium text-[var(--color-text-primary)] mt-3 mb-2">
                  Allergies <span className="font-normal text-[var(--color-text-secondary)]">(optional)</span>
                </label>
                <input
                  type="text"
                  value={allergyNote}
                  onChange={(e) => setAllergyNote(e.target.value)}
                  placeholder="e.g. Peanut allergy"
                  maxLength={500}
                  disabled={modificationState.isModifying && !modificationState.canModify}
                  className="w-full px-3 py-2 bg-[var(--color-background-default)] border border-[var(--color-border-default)] rounded-lg text-[var(--color-text-primary)] placeholder-[var(--color-text-secondary)] focus:ring-2 focus:ring-[var(--color-brand-purple)] focus:border-transparent text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>

              {/* Order Summary */}
              <div className="border-t border-[var(--color-border-default)] pt-4">
                <h3 className="font-semibold text-[var(--color-text-primary)] mb-3">Order Summary</h3>
                <div className="space-y-2">
                  {/* Subtotal */}
                  <div className="flex justify-between text-sm">
                    <span className="text-[var(--color-text-secondary)]">Subtotal</span>
                    <span className="text-[var(--color-text-primary)]">
                      {CURRENCY.symbol}{(taxPreview?.subtotal || total).toFixed(2)}
                    </span>
                  </div>

                  {/* Tax Breakdown - Always show tax section for transparency */}
                  {taxLoading ? (
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--color-text-secondary)] text-xs italic">Calculating taxes...</span>
                      <span className="text-[var(--color-text-secondary)] text-xs italic">...</span>
                    </div>
                  ) : taxPreview ? (
                    <>
                      {/* Discount — before taxes/service charge: they are computed on
                          the discounted base, so display order mirrors calculation order */}
                      {taxPreview.discountAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--color-text-secondary)]">Discount</span>
                          <span className="text-green-600">-{CURRENCY.symbol}{taxPreview.discountAmount.toFixed(2)}</span>
                        </div>
                      )}

                      {/* CGST - Show even if 0 for transparency */}
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--color-text-secondary)]">
                          CGST ({taxPreview.cgstRate || 0}%)
                        </span>
                        <span className="text-[var(--color-text-primary)]">{CURRENCY.symbol}{(taxPreview.cgstAmount || 0).toFixed(2)}</span>
                      </div>

                      {/* SGST - Show even if 0 for transparency */}
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--color-text-secondary)]">
                          SGST ({taxPreview.sgstRate || 0}%)
                        </span>
                        <span className="text-[var(--color-text-primary)]">{CURRENCY.symbol}{(taxPreview.sgstAmount || 0).toFixed(2)}</span>
                      </div>

                      {/* IGST - Only show if applicable (inter-state) */}
                      {(taxPreview.igstRate > 0 || taxPreview.igstAmount > 0) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--color-text-secondary)]">
                            IGST ({taxPreview.igstRate}%)
                          </span>
                          <span className="text-[var(--color-text-primary)]">{CURRENCY.symbol}{taxPreview.igstAmount.toFixed(2)}</span>
                        </div>
                      )}

                      {/* Service Charge - Show even if 0 for transparency */}
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--color-text-secondary)]">
                          Service Charge ({taxPreview.serviceChargeRate || 0}%)
                        </span>
                        <span className="text-[var(--color-text-primary)]">{CURRENCY.symbol}{(taxPreview.serviceChargeAmount || 0).toFixed(2)}</span>
                      </div>

                    </>
                  ) : (
                    // Show default structure when tax data is not available
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--color-text-secondary)]">CGST (0%)</span>
                        <span className="text-[var(--color-text-primary)]">{CURRENCY.symbol}0.00</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--color-text-secondary)]">SGST (0%)</span>
                        <span className="text-[var(--color-text-primary)]">{CURRENCY.symbol}0.00</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--color-text-secondary)]">Service Charge (0%)</span>
                        <span className="text-[var(--color-text-primary)]">{CURRENCY.symbol}0.00</span>
                      </div>
                    </>
                  )}

                  {/* Total */}
                  <div className="border-t border-[var(--color-border-default)] pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-[var(--color-text-primary)]">Total</span>
                      <span className="font-bold text-lg text-[var(--color-text-primary)]">
                        {CURRENCY.symbol}{(taxPreview?.total || total).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {/* Action Button - Desktop Only */}
              <button
                onClick={handlePay}
                disabled={loading || cartState.items.length === 0 || (modificationState.isModifying && !modificationState.canModify)}
                className="w-full mt-6 py-3 px-6 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold rounded-xl transition-colors hidden lg:flex items-center justify-center space-x-2"
                style={{
                  backgroundColor: theme.button.primary.solidBg || theme.button.primary.bg,
                  color: theme.button.primary.text,
                }}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>
                      {modificationState.isModifying && modificationState.canModify 
                        ? `Update Order • ${CURRENCY.symbol}${total.toFixed(2)}`
                        : `Pay & Place Order • ${CURRENCY.symbol}${total.toFixed(2)}`
                      }
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom spacing to prevent content from being hidden behind sticky footer on mobile */}
      {isMobile && (
        <div 
          style={{
            paddingBottom: 'max(20px, calc(20px + env(safe-area-inset-bottom, 0px)))',
            minHeight: '20px'
          }}
        />
      )}

      {/* Sticky Footer - Mobile Only */}
      {isMobile && (
        <div 
          className="fixed left-0 right-0 bg-[var(--color-background-surface)] border-t border-[var(--color-border-default)]"
          style={{
            bottom: 0,
            padding: '16px',
            paddingBottom: 'max(16px, calc(16px + env(safe-area-inset-bottom, 0px)))',
            paddingLeft: 'max(16px, calc(16px + env(safe-area-inset-left, 0px)))',
            paddingRight: 'max(16px, calc(16px + env(safe-area-inset-right, 0px)))',
            minHeight: 'max(64px, calc(64px + env(safe-area-inset-bottom, 0px)))',
            boxSizing: 'border-box'
          }}
        >
          <button
            onClick={handlePay}
            disabled={loading || cartState.items.length === 0 || (modificationState.isModifying && !modificationState.canModify)}
            className="w-full py-3 px-6 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
            style={{
              backgroundColor: theme.button.primary.solidBg || theme.button.primary.bg,
              color: theme.button.primary.text,
            }}
          >
            {loading ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>
                  {modificationState.isModifying && modificationState.canModify 
                    ? `Update Order • ${CURRENCY.symbol}${total.toFixed(2)}`
                    : `Pay & Place Order • ${CURRENCY.symbol}${total.toFixed(2)}`
                  }
                </span>
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Authentication Modal */}
      {renderAuthModal()}

      {/* Simple Guest Name Modal (for allowed guest table orders) */}
      {renderGuestNameModal()}

      {/* Guest Verification Modal */}
      <ImprovedGuestVerificationModal
        isOpen={showGuestModal}
        onClose={() => setShowGuestModal(false)}
        onSuccess={handleGuestSuccess}
        vendorName="our restaurant"
      />

      {/* Payment Difference Modal */}
      {showPaymentModal && modificationState.originalOrderId && (
        <PaymentDifferenceModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onPaymentComplete={handlePaymentComplete}
          orderId={modificationState.originalOrderId}
          originalTotal={0} // Will be fetched in the modal
          newTotal={total}
        />
      )}
      
      {/* Order Confirmation Dialog */}
      <OrderConfirmationDialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        onConfirm={handleConfirmOrder}
        qrCode={qrCode}
        isLoading={loading}
        guestName={guestName}
        taxPreview={taxPreview}
      />
      
      {/* Session Conflict Modal */}
      <SessionConflictModal
        isOpen={showSessionConflictModal}
        onClose={() => {
          setShowSessionConflictModal(false);
          // If user continues with existing session, show confirmation
          if (isAuthenticated) {
            setShowConfirmationDialog(true);
          }
        }}
        onContinueAsGuest={() => {
          setShowSessionConflictModal(false);
          setShowGuestModal(true);
        }}
        onLoginDifferent={() => {
          setShowSessionConflictModal(false);
          setShowAuthModal(true);
        }}
        existingSession={(() => {
          const guestSessionStr = localStorage.getItem('guestSession');
          const userStr = localStorage.getItem('user');
          
          if (guestSessionStr) {
            try {
              const session = JSON.parse(guestSessionStr);
              return {
                type: 'guest' as const,
                name: session.name || 'Guest',
                phone: session.phoneNumber
              };
            } catch (_e) {
              console.error('Failed to parse guest session');
            }
          }
          
          if (userStr) {
            try {
              const user = JSON.parse(userStr);
              return {
                type: 'user' as const,
                name: user.username,
                email: user.email
              };
            } catch (_e) {
              console.error('Failed to parse user');
            }
          }
          
          return null;
        })()}
      />
    </div>
  );
};

export default Checkout;
